import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Clock, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { TimeEntryPopup } from "./time-entry-popup";
import { getCategoryById } from "@/lib/time-categories";

interface ProjectTimeSummaryPanelProps {
  projectId: string;
  timeSummary: any;
}

export function ProjectTimeSummaryPanel({ projectId, timeSummary }: ProjectTimeSummaryPanelProps) {
  const [selectedPulseId, setSelectedPulseId] = useState<number | null>(null);
  const [timeEntryPopupOpen, setTimeEntryPopupOpen] = useState(false);
  const [timeEntryPosition, setTimeEntryPosition] = useState({ x: 0, y: 0 });
  const [expandedPulses, setExpandedPulses] = useState<Set<number>>(new Set());

  // Fetch project pulses to allow time tracking
  const { data: projectData, isLoading: pulsesLoading } = useQuery<any>({
    queryKey: [`/api/projects/${projectId}`],
  });

  // Helper to format minutes
  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  const handleTrackTime = (pulseId: number, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setSelectedPulseId(pulseId);
    setTimeEntryPosition({ x: rect.right + 10, y: rect.top });
    setTimeEntryPopupOpen(true);
  };

  const togglePulseExpanded = (pulseId: number) => {
    const newExpanded = new Set(expandedPulses);
    if (newExpanded.has(pulseId)) {
      newExpanded.delete(pulseId);
    } else {
      newExpanded.add(pulseId);
    }
    setExpandedPulses(newExpanded);
  };

  // Get pulses with time entries
  const pulsesWithTime = timeSummary?.byPulse || [];
  const pulses = projectData?.pulses || [];
  
  // Combine pulses with their time data
  const pulsesData = pulses.map((pulse: any) => {
    const timeData = pulsesWithTime.find((p: any) => p.pulseId === pulse.id);
    return {
      ...pulse,
      timeData: timeData || { totalMinutes: 0, byType: [] }
    };
  });

  const totalMinutes = timeSummary?.totalMinutes || 0;
  const timeByType = timeSummary?.byType || [];

  return (
    <>
      <div className="space-y-6">
        {/* Overall Summary */}
        <div className="border rounded-lg p-4 bg-gray-800/50">
          <h3 className="text-sm font-semibold mb-3">Total Project Time</h3>
          <div className="text-2xl font-bold mb-3">
            {formatMinutes(totalMinutes)}
          </div>
          
          {timeByType.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {timeByType
                .filter((entry: any) => entry.totalMinutes > 0)
                .map((entry: any) => {
                  const category = getCategoryById(entry.timeTypeId);
                  
                  return (
                    <div
                      key={entry.timeTypeId}
                      className="flex items-center justify-between p-2 rounded text-sm"
                      style={{ backgroundColor: category.color + '40' }}
                    >
                      <span className="text-white font-medium">{category.name}</span>
                      <span className="text-white/90">{formatMinutes(entry.totalMinutes)}</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Per-Pulse Breakdown */}
        <div className="border rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3">Time by Pulse</h3>
          
          {pulsesLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading pulses...
            </div>
          ) : pulsesData.length === 0 ? (
            <div className="text-sm text-gray-400">
              No pulses found. Create a pulse to start tracking time.
            </div>
          ) : (
            <div className="space-y-2">
              {pulsesData.map((pulse: any) => (
                <div
                  key={pulse.id}
                  className="border border-gray-600 rounded p-3 hover:bg-gray-700/30 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {pulse.timeData.totalMinutes > 0 && (
                          <button
                            onClick={() => togglePulseExpanded(pulse.id)}
                            className="text-gray-400 hover:text-white transition-colors"
                            data-testid={`expand-pulse-${pulse.id}`}
                          >
                            {expandedPulses.has(pulse.id) ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {pulse.pulseDescription || `Pulse #${pulse.id}`}
                          </div>
                          <div className="text-xs text-gray-400">
                            {pulse.status}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {pulse.timeData.totalMinutes > 0 && (
                        <div className="text-sm font-medium text-white">
                          {formatMinutes(pulse.timeData.totalMinutes)}
                        </div>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleTrackTime(pulse.id, e)}
                        className="gap-1"
                        data-testid={`track-time-btn-${pulse.id}`}
                      >
                        <Clock className="w-3 h-3" />
                        {pulse.timeData.totalMinutes > 0 ? 'Add' : 'Track'}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded time breakdown */}
                  {expandedPulses.has(pulse.id) && pulse.timeData.byType?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-600 space-y-1">
                      {pulse.timeData.byType.map((entry: any) => {
                        const category = getCategoryById(entry.timeTypeId);
                        
                        return (
                          <div
                            key={entry.timeTypeId}
                            className="flex items-center justify-between px-2 py-1 rounded text-xs"
                            style={{ backgroundColor: category.color + '20' }}
                          >
                            <span className="text-white/90">{category.name}</span>
                            <span className="text-white font-medium">
                              {formatMinutes(entry.totalMinutes)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help text */}
        <div className="text-xs text-gray-400 italic">
          Time entries are tracked at the pulse level. Click "Track" next to any pulse to add time.
          Project totals show the sum of all pulse time entries.
        </div>
      </div>

      {/* Time Entry Popup */}
      {selectedPulseId && (
        <TimeEntryPopup
          projectId={projectId}
          pulseId={selectedPulseId}
          open={timeEntryPopupOpen}
          onOpenChange={setTimeEntryPopupOpen}
          mousePosition={timeEntryPosition}
        />
      )}
    </>
  );
}
